// BRK-S140 — a project address as a link to a map, with NO map SDK.
//
// A bond order's project address is a place a broker (and an underwriter) wants
// to look at, and the cheapest honest way to offer that is a search URL: it
// opens whatever map the viewer already uses on whatever device they are on, it
// needs no API key, it ships no bytes, and it cannot leak an address to a
// third-party script — because there is no script. A JS Maps embed would have
// meant a keyed loader, a consent story for an iframe that phones Google on
// every render, and a control nobody asked for.
//
// The key that DOES exist in this repo (`VITE_GOOGLE_PLACES_BROWSER_KEY`,
// BRK-S067) is for the Places autocomplete and is deliberately not reused here:
// a search URL needs no key at all, so requiring one would make the link
// disappear in any environment that has not configured one.

import { formatAddress, type AddressParts } from "@/lib/address";

const SEARCH_BASE = "https://www.google.com/maps/search/?api=1&query=";

/**
 * A Google Maps search URL for an address, or **null** when there is not enough
 * of an address to search for.
 *
 * Null rather than a link to nothing: a "View on map" that opens a search for
 * `, , ` lands the broker on an empty map in another tab, which reads as a
 * broken portal rather than as a missing value. The bar is a STREET and a CITY —
 * a state alone is a link to a state.
 *
 * The address is passed as ONE `query` string rather than as components,
 * because that is what the documented search endpoint takes, and it is
 * `encodeURIComponent`-escaped: an address is user-entered text, and a `#` in a
 * suite number would otherwise truncate the query at the fragment.
 */
export function googleMapsSearchUrl(
  parts: AddressParts | null | undefined,
): string | null {
  if (!parts) return null;
  if (!parts.street?.trim() || !parts.city?.trim()) return null;
  // The same string the record shows, so the link and the text can never
  // describe two different places.
  return `${SEARCH_BASE}${encodeURIComponent(formatAddress(parts))}`;
}
